import { PaginatedDataDTO, PaginationDTO } from '@bottega52/commons-pagination';
import { AppBar, Button, Tab, Tabs } from '@material-ui/core';
import { createStyles, WithStyles, withStyles } from '@material-ui/core/styles';
import { AccountBalanceOutlined, Business, SearchOutlined } from '@material-ui/icons';
import CreditsIcon from '@material-ui/icons/MonetizationOn';
import CachedIcon from '@material-ui/icons/Cached';
import WarningIcon from '@material-ui/icons/Warning';
import { Stack } from '@mui/material';
import { DataGrid, GridColumns } from '@mui/x-data-grid';
import React from 'react';
import * as CustomerPlantDecoder from '../codec/customerDomainDecoder';
import { connect, ConnectedProps } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import DomainDetailsView from '../components/Domains/DomainDetailsView';
import UserImpersonateForm from '../components/Domains/DomainUsers/UserImpersonateForm';
import AddPluginForm from '../components/Forms/AddPluginForm';
import NewDomainUserForm from '../components/Forms/NewDomainUserForm';
import { ModalTypes } from '../components/Modals/ModalTypes';
import Permission from '../components/Permission/Permission';
import ReduxLanguage from '../components/ReduxLanguage';
import SearchBar from '../components/SearchBar/SearchBar';
import * as CreditsActions from '../redux/credits/credits.actions';
import * as DomainActions from '../redux/domains/domains.actions';
import * as DomainsActions from '../redux/domains/domains.actions';
import { DomainsActions as DomainsBaseAction } from '../redux/domains/domains.actions';
import * as ModalsActions from '../redux/modals/modals.actions';
import * as ProductActions from '../redux/products/products.actions';
import * as SettingsActions from '../redux/settings/settings.actions';
import { IState } from '../redux/store';
import * as UserActions from '../redux/user/user.actions';
import { IDomainDetailedDTO } from '../repository/jago/model/input/IDomainDetailedDTO';
import { DomainIrisStatusOptions, ICustomerDomainFormDTO, IDomainInDTO } from '../repository/jago/model/input/IDomainInDTO';
import { IDomainUserInDTO } from '../repository/jago/model/input/IDomainUserInDTO';
import { IProductInDTO } from '../repository/jago/model/input/IProductInDTO';
import { IDomainUserOutDTO } from '../repository/jago/model/output/IDomainUserOutDTO';
import { getDomainFrontendAddressFromHostname, getDomainNameFromHostname } from '../repository/utils';
import AbilityProvider from '../services/PermissionService/AbilityProvider';
import { PERMISSIONS, Roles, SUPPORT_AGENT_READ_ONLY_EMAIL } from '../services/PermissionService/PermissionConstants';
import { BLUE, ORANGE } from '../style/StyleConsts';
import translations from '../translations/i18next';
import CustomerPlantForm from '../components/Forms/CustomerPlantForm';
import EditPlantSOCDetailsForm from '../components/Forms/EditPlantSOCDetailsForm';
import Utils from '../utils/Utils';
import UserDeleteForm from '../components/Domains/DomainUsers/UserDeleteForm';
import { IPlantDetailsUpdateDTO } from '../repository/jago/domainsAPI';

const styles = createStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    padding: 0,
  },
  text: {
    margin: 0,
    marginBottom: 15,
  },
  logo: {
    width: '20%',
  },
  innerHeader: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  header: {
    backgroundColor: 'white',
    width: '100%',
    zIndex: 200,
    padding: 10,
    paddingRight: 20,
    borderBottom: '2px solid #5AC0B1',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  federatedContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 5
  },
  tableContainer: {
    display: 'flex',
    backgroundColor: 'white',
    height: 'calc(100vh - 160px)',
    width: '100%'
  },
  rowTitle: {
    fontWeight: 'bold'
  },
  domainTypeContainer: {
    position: 'absolute',
    right: 10,
    top: 10,
  },
  domainTypeInnerContainer: {
    padding: 5,
    backgroundColor: BLUE,
    borderRadius: 5,
  },
  domainIrisContainer: {
  },
  domainIrisInnerContainer: {
    padding: 5,
    borderRadius: 5,
  },
  domainTypeText: {
    fontWeight: 'bold',
    margin: 0
  }
});


export interface IDomainPageState {
  searchText: string;
  pageSize: number;
  isLoading: boolean;
  activeTab: number;
}

type IReduxProps = ConnectedProps<typeof connector> & RouteComponentProps<any>;

export interface IDomainsPageProps extends WithStyles<typeof styles> {
  language: string;
  domains: PaginatedDataDTO<IDomainInDTO>;
  periodicDomains: PaginatedDataDTO<IDomainInDTO>;
  legacyDomains: PaginatedDataDTO<IDomainInDTO>;
  detailedDomain: IDomainDetailedDTO;
  products: IProductInDTO[];
}

export type ComponentProps = IDomainsPageProps & IReduxProps;

export class DomainsPage extends React.Component<ComponentProps, IDomainPageState> {
  private domainsColumns: GridColumns<IDomainInDTO>;
  constructor(props: ComponentProps) {
    super(props);
    this.state = {
      searchText: '',
      pageSize: 100,
      isLoading: false,
      activeTab: 0,
    }
  }

  public async componentDidMount() {
    const { dispatch } = this.props;
    const { pageSize } = this.state;
    try {
      this.setState({ isLoading: true });
      await this.onFetchDomains(0, pageSize);
      if (AbilityProvider.getAbilityHelper().hasPermission(PERMISSIONS.DOMAIN_EXTEND)) {
        dispatch<any>(ProductActions.fetchSofiaProducts());
      }
      this.setState({ isLoading: false });
    } catch (error) {
      this.setState({ isLoading: false });
    }
  }

  async onFetchDomains(page: number = 0, pageSize: number = 100) {
    const { dispatch } = this.props;
    const { searchText, activeTab } = this.state;
    let params = { page, pageSize, hostname: searchText, hostName: searchText, walletId: undefined, isCloudCredits: false };
    try {
      this.setState({ isLoading: true });
      if (activeTab === 0) {
        await dispatch<any>(CreditsActions.fetchCustomerWalletPlants({...params, modality: 'CREDITS'}, true));
      } 
      else if (activeTab === 1) {
        await dispatch<any>(CreditsActions.fetchCustomerPeriodicPlants({...params, modality: 'PERIODIC'}));
      } 
      else {
        await dispatch<any>(DomainsActions.fetchSofiaDomains(params));
      }
      this.setState({ isLoading: false });
    } catch (error) {
      this.setState({ isLoading: false });
    }
  }

  public onDeletePlantRequest(plant: IDomainDetailedDTO) {
    const { dispatch } = this.props;
    dispatch<any>(ModalsActions.showModal(`DELETE_PLANT_CONFIRM_MODAL`, {
      modalType: ModalTypes.CONFIRM_MODAL,
      modalProps: {
        icon: <WarningIcon style={{ color: 'orange', fontSize: 50 }} />,
        titleMessageKey: 'domains.domainDeletionTitle',
        successMessageKey: 'domains.domainDeletionConfirm',
        confirmMessageKey: 'domains.sendDeletionRequest',
        cancelMessageKey: 'forms.cancel',
        onConfirm: () => this.onDeletePlantConfirm(plant),
        onCancel: () => dispatch<any>(ModalsActions.hideModal(`DELETE_PLANT_CONFIRM_MODAL`)),
      }
    }));
  }

  public async onDeletePlantConfirm(plant: IDomainDetailedDTO) {
    const { dispatch } = this.props;
    try {
      const domainName = plant.hostname
      dispatch<any>(ModalsActions.hideModal(`DELETE_PLANT_CONFIRM_MODAL`));
      dispatch<any>(SettingsActions.setSpinnerVisible(true));
      await dispatch<any>(UserActions.openCloseDomainTicket(domainName));
      dispatch<any>(ModalsActions.hideModal('DOMAIN_DETAILS_MODAL'));
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      dispatch<any>(ModalsActions.showModal(`SUCCESS_MODAL`, {
        modalType: ModalTypes.SUCCESS_MODAL,
        modalProps: {
          titleMessageKey: 'domains.domainDeletionSuccess',
        }
      }));
    } catch (error) {
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      dispatch<any>(ModalsActions.showModal(`ERROR_DOMAIN_DELETION`, {
        modalType: ModalTypes.ERROR_MODAL,
        modalProps: {
          titleMessageKey: 'errors.error',
          errorMessageKey: 'errors.deleteDomainRequestError',
        }
      }));
    }
  }


  public async updateDomainConfiguration(config: string) {
    const { dispatch, detailedDomain } = this.props;
    try {
      dispatch<any>(SettingsActions.setSpinnerVisible(true));
      await dispatch<any>(DomainsActions.updateSofiaDomainConfiguration(detailedDomain.hostname, { data: config }));
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      dispatch<any>(ModalsActions.showModal(`SUCCESS_MODAL`, {
        modalType: ModalTypes.SUCCESS_MODAL,
        modalProps: {
          titleMessageKey: 'domains.domainConfigModifySuccess',
        }
      }));
      await dispatch<any>(DomainsActions.fetchSofiaDomainConfiguration());
    } catch (error) {
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      dispatch<any>(ModalsActions.showModal(`ERROR_DOMAIN_CONFIG_UPDATE`, {
        modalType: ModalTypes.ERROR_MODAL,
        modalProps: {
          titleMessageKey: 'errors.error',
          errorMessageKey: 'errors.updateDomainConfigError',
        }
      }));
    }
  }

  public async impersonateDomainUserRequest(user: IDomainUserInDTO) {
    const { dispatch, detailedDomain } = this.props;
    const isDomainImpersonateWriteEnabled = !(Utils.hasEngineSubscription(detailedDomain.walletSubscriptions) && AbilityProvider.getAbilityHelper().hasRoles([Roles.ROLE_VASHO_VAR_SUPPORT, Roles.ROLE_VASHO_VAR_ADMIN]))

    dispatch<any>(ModalsActions.showModal(`IMPERSONATE_DOMAIN_CONFIRM_MODAL`, {
      modalType: ModalTypes.CONTENT_MODAL,
      modalProps: {
        content: (
        <UserImpersonateForm
          user={user}
          onImpersonateReadyOnly={(duration) => this.impersonateDomainUser(SUPPORT_AGENT_READ_ONLY_EMAIL, duration)}
          onImpersonateUser={(duration) => this.impersonateDomainUser(user.email, duration)}
          isDomainImpersonateWriteEnabled={isDomainImpersonateWriteEnabled}
        />
        ),
      }
    }));
  }

  public async impersonateDomainUser(userEmail: string, duration?: number) {
    const { dispatch, detailedDomain } = this.props;
    try {
      dispatch<any>(SettingsActions.setSpinnerVisible(true));
      const userToken = await dispatch<any>(DomainsActions.impersonateDomainUser(detailedDomain.hostname, userEmail, duration));
      const frontendURL = getDomainFrontendAddressFromHostname(detailedDomain.hostname);
      window.open(`https://${frontendURL}?p=${userToken}`);
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      dispatch<any>(ModalsActions.showModal(`SUCCESS_MODAL`, {
        modalType: ModalTypes.SUCCESS_MODAL,
        modalProps: {
          titleMessageKey: 'domains.impersonateSuccess',
        }
      }));
    } catch (error) {
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      dispatch<any>(ModalsActions.showModal(`ERROR_DOMAIN_IMPERSONATE`, {
        modalType: ModalTypes.ERROR_MODAL,
        modalProps: {
          titleMessageKey: 'errors.error',
          errorMessageKey: 'errors.impersonateDomainUserError',
        }
      }));
    }
  }

  public async onUpdateAddionalInfo(note: string) {
    const { dispatch, detailedDomain } = this.props;
    try {
      dispatch<any>(SettingsActions.setSpinnerVisible(true));
      const outDTO = {
        note,
      }
      await dispatch<any>(DomainsActions.updateSofiaDomainAdditionalInfo(detailedDomain, outDTO));
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      dispatch<any>(ModalsActions.showModal(`SUCCESS_MODAL`, {
        modalType: ModalTypes.SUCCESS_MODAL,
        modalProps: {
          titleMessageKey: 'domains.additionalInfoSavedSuccessfully',
        }
      }));
    } catch (error) {
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      dispatch<any>(ModalsActions.showModal(`ERROR_DOMAIN_IMPERSONATE`, {
        modalType: ModalTypes.ERROR_MODAL,
        modalProps: {
          titleMessageKey: 'errors.error',
          errorMessageKey: 'errors.errorSaveAdditionalInfo',
        }
      }));
    }
  }

  public onOpenAddPluginForm() {
    const { dispatch, products } = this.props;
    dispatch<any>(ModalsActions.showModal(`OPERATIONAL_EXTENDS_DOMAIN_MODAL`, {
      modalType: ModalTypes.OPERATIONAL_VIEW_MODAL,
      modalProps: {
        content: (
          <AddPluginForm
            products={products}
            onAddNewPlugin={(productData: IProductInDTO) => this.onAddNewPluginRequest(productData)}
          />
        ),
        titleMessageKey: 'domains.extendDomain',
      }
    }));
  }


  public async appenDomainUsers(page: number) {
    const { dispatch } = this.props;
    await dispatch<any>(DomainsActions.fetchSofiaDomainUsers({ page, pageSize: 50 }));
  }
  
  public async onRowClick(domain: IDomainInDTO) {
    const { dispatch, history } = this.props;
    try {
      if (domain.modality === 'PERIODIC') {
        this.onOpenEditCustomerPlantForm(domain);
        return;
      }
      dispatch<any>(SettingsActions.setSpinnerVisible(true));
      dispatch<any>(DomainsBaseAction.setSelectedDetailedDomain(domain));
      await dispatch<any>(DomainsActions.fetchSofiaDomainConfiguration());
      if (!domain.cloudCredits) {
        await dispatch<any>(DomainsActions.fetchSofiaDomainSubscription());
      }
      await dispatch<any>(DomainsActions.fetchSofiaDomainUsers({ pageSize: 50 }));
      await dispatch<any>(DomainsActions.fetchSofiaDomainInstallers({ pageSize: 50 }));
      await dispatch<any>(DomainsActions.fetchDomainWalletSubscriptions());
      await dispatch<any>(DomainsActions.fetchSofiaDomainInfo());
      await dispatch<any>(DomainsActions.fetchSofiaDomainCustomer());
      await dispatch<any>(DomainsActions.fetchSofiaDomainAdditionalInfo());
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      const { detailedDomain } = this.props;
      dispatch<any>(ModalsActions.showModal(`OPERATIONAL_DOMAIN_MODAL`, {
        modalType: ModalTypes.OPERATIONAL_VIEW_MODAL,
        modalProps: {
          onClose: () => dispatch<any>(DomainsActions.DomainsActions.setSelectedDetailedDomain({})),
          content: (
            <DomainDetailsView
              detailedDomain={detailedDomain}
              onSaveConfig={(config: string) => this.updateDomainConfiguration(config)}
              onImpersonateUser={(user: IDomainUserInDTO) => this.impersonateDomainUserRequest(user)}
              onAppenDomainUsers={(page: number) => this.appenDomainUsers(page)}
              onNewUserClick={() => this.onNewUserClick()}
              onNewInstallerClick={() => this.onNewInstallerClick()}
              onDeleteDomainUserRequest={(user: IDomainUserInDTO) => this.onDeleteDomainUserRequest(user)}
              onDeletePlantRequest={() => this.onDeletePlantRequest((detailedDomain) as IDomainDetailedDTO)}
              onExtendDomainClick={() => this.onOpenAddPluginForm()}
              onUpdateAddionalInfo={value => this.onUpdateAddionalInfo(value)}
              onOpenCustomerDetails={customerId => {
                dispatch<any>(ModalsActions.hideModal(`OPERATIONAL_DOMAIN_MODAL`));
                history.push(`/customers?customerId=${customerId}`);
              }}
              onGatewaysClick={() => {
                if (detailedDomain.hostname) {
                  dispatch<any>(ModalsActions.hideModal(`OPERATIONAL_DOMAIN_MODAL`));
                  history.push(`/gateways?hostname=${detailedDomain.hostname}`);
                }
              }}
              onEditPlantSocDetails={(domain: IDomainDetailedDTO) => this.onOpenEditPlantSocDetailsForm(domain)}
            />
          ),
          titleMessageKey: 'domains.domainDetails',
        }
      }));
    } catch (error) {
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      dispatch<any>(ModalsActions.showModal(`ERROR_DOMAIN_DETAILS`, {
        modalType: ModalTypes.ERROR_MODAL,
        modalProps: {
          titleMessageKey: 'errors.error',
          errorMessageKey: 'errors.getDomainDetailsError',
        }
      }));
    }
  }

  async onOpenEditCustomerPlantForm(customerPlant: IDomainInDTO) {
    const { dispatch } = this.props;
    if (customerPlant.solution === 'LUCKEY' && customerPlant.management === 'SAAS')
      return;
    const customerDomainFormData = CustomerPlantDecoder.decodeCustomerPlantContentToForm(customerPlant);
    dispatch<any>(ModalsActions.showModal(`OPERATIONAL_CUSTOMER_DOMAIN_MODAL`, {
      modalType: ModalTypes.OPERATIONAL_VIEW_MODAL,
      modalProps: {
        content: (
          <CustomerPlantForm
            customerId={customerDomainFormData.customerId}
            customerPlant={customerDomainFormData}
            onCreateEditCustomerPlant={(newCustomerPlantData: ICustomerDomainFormDTO) => this.onEditCustomerPlantRequest(customerPlant.id, newCustomerPlantData)}
          />
        ),
        titleMessageKey: 'credits.customerDomainFormTitle',
      }
    }));
  }

  public onEditCustomerPlantRequest(customerDomainId: number, newCustomerDomainData: ICustomerDomainFormDTO) {
    const { dispatch } = this.props;
    dispatch<any>(ModalsActions.showModal(`CREATE_CUSTOMER_DOMAIN_CONFIRM_MODAL`, {
      modalType: ModalTypes.CONFIRM_MODAL,
      modalProps: {
        icon: <WarningIcon style={{ color: 'orange', fontSize: 50 }} />,
        titleMessageKey: 'credits.editCustomerDomain',
        successMessageKey: 'credits.editCustomerDomainConfirm',
        confirmMessageKey: 'forms.confirm',
        cancelMessageKey: 'forms.cancel',
        onConfirm: () => this.onEditCustomerPlantConfirm(customerDomainId, newCustomerDomainData),
        onCancel: () => dispatch<any>(ModalsActions.hideModal(`CREATE_CUSTOMER_DOMAIN_CONFIRM_MODAL`)),
      }
    }));
  }

  public async onEditCustomerPlantConfirm(customerDomainId: number, newCustomerDomainData: ICustomerDomainFormDTO) {
    const { dispatch } = this.props;
    const { pageSize } = this.state;
    try {
      dispatch<any>(ModalsActions.hideModal(`CREATE_CUSTOMER_DOMAIN_CONFIRM_MODAL`));
      dispatch<any>(SettingsActions.setSpinnerVisible(true));
      const newCustomerDomainDataEncoded = CustomerPlantDecoder.encodeCustomerPlantFromForm(newCustomerDomainData);
      const customerDomainUpdated = await dispatch<any>(CreditsActions.editCustomerPlant(customerDomainId, newCustomerDomainDataEncoded));
      dispatch<any>(ModalsActions.hideModal(`OPERATIONAL_CUSTOMER_DOMAIN_MODAL`));
      dispatch<any>(CreditsActions.setSelectedCustomerPlant(customerDomainUpdated));
      await dispatch<any>(CreditsActions.fetchCustomerPlants({ page: 0, pageSize: pageSize, customerId: newCustomerDomainData.customerId }));
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
    } catch (error) {
      console.log(error)
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      dispatch<any>(ModalsActions.showModal(`ERROR_CUSTOMER_DOMAIN_CREATION`, {
        modalType: ModalTypes.ERROR_MODAL,
        modalProps: {
          titleMessageKey: 'errors.error',
          errorMessageKey: 'credits.createCustomerDomainError',
        }
      }));
    }
  }

  public onAddNewPluginRequest(productData:IProductInDTO) {
    const { dispatch } = this.props;
    dispatch<any>(ModalsActions.showModal(`CREATE_DOMAIN_PLUGIN_CONFIRM_MODAL`, {
      modalType: ModalTypes.CONFIRM_MODAL,
      modalProps: {
        icon: <WarningIcon style={{ color: 'orange', fontSize: 50 }} />,
        titleMessageKey: 'domains.extendDomain',
        successMessageKey: 'forms.domainExtendsConfirm',
        confirmMessageKey: 'forms.confirm',
        cancelMessageKey: 'forms.cancel',
        onConfirm: () => this.onAddNewPluginConfirm(productData),
        onCancel: () => dispatch<any>(ModalsActions.hideModal(`CREATE_DOMAIN_PLUGIN_CONFIRM_MODAL`)),
      }
    }));
  }

  public async onAddNewPluginConfirm(productData:IProductInDTO) {
    const { dispatch } = this.props;
    try {
      dispatch<any>(ModalsActions.hideModal(`CREATE_DOMAIN_PLUGIN_CONFIRM_MODAL`));
      dispatch<any>(SettingsActions.setSpinnerVisible(true));
      await dispatch<any>(DomainsActions.addProductToDomain(productData));
      await dispatch<any>(DomainsActions.fetchSofiaDomainSubscription());
      dispatch<any>(ModalsActions.hideModal('OPERATIONAL_EXTENDS_DOMAIN_MODAL'));
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
    } catch (error) {
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      dispatch<any>(ModalsActions.showModal(`ERROR_DOMAIN_EXTENSION`, {
        modalType: ModalTypes.ERROR_MODAL,
        modalProps: {
          titleMessageKey: 'errors.error',
          errorMessageKey: 'errors.addDomainPluginError',
        }
      }));
    }
    
  }

  onNewUserClick() {
    const { dispatch } = this.props;
    dispatch<any>(ModalsActions.showModal(`OPERATIONAL_NEW_USER_DOMAIN_MODAL`, {
      modalType: ModalTypes.OPERATIONAL_VIEW_MODAL,
      modalProps: {
        content: (
          <NewDomainUserForm
            roleId={1}
            onCreateNewUser={(user: IDomainUserOutDTO) => this.onCreateUserFormDomain(user)}
          />
        ),
        titleMessageKey: 'forms.newUser',
      }
    }));
  }

  onNewInstallerClick() {
    const { dispatch } = this.props;
    dispatch<any>(ModalsActions.showModal(`OPERATIONAL_NEW_USER_DOMAIN_MODAL`, {
      modalType: ModalTypes.OPERATIONAL_VIEW_MODAL,
      modalProps: {
        content: (
          <NewDomainUserForm
            roleId={2}
            onCreateNewUser={(user: IDomainUserOutDTO) => this.onCreateUserFormDomain(user)}
          />
        ),
        titleMessageKey: 'forms.newInstaller',
      }
    }));
  }

  onCreateUserAfterDomainCreation(newDomain: IDomainInDTO) {
    const { dispatch } = this.props;
    dispatch<any>(DomainsBaseAction.setSelectedDetailedDomain(newDomain));
    dispatch<any>(ModalsActions.hideModal('CREATE_USER_AFTER_DOMAIN_MODAL'));
    this.onNewUserClick();
  }

  async onCreateUserFormDomain(user: IDomainUserOutDTO) {
    const { dispatch } = this.props;
    try {
      dispatch<any>(SettingsActions.setSpinnerVisible(true));
      await dispatch<any>(DomainsActions.createNewUserForDomain(user));
      await dispatch<any>(DomainsActions.fetchSofiaDomainUsers({ pageSize: 50 }));
      await dispatch<any>(DomainsActions.fetchSofiaDomainInstallers({ pageSize: 50 }));
      await dispatch<any>(DomainsActions.fetchDomainWalletSubscriptions());
      await dispatch<any>(DomainsActions.fetchSofiaDomainSubscription());
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      dispatch<any>(ModalsActions.hideModal('OPERATIONAL_NEW_USER_DOMAIN_MODAL'));
    } catch (error) {
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      dispatch<any>(ModalsActions.showModal(`ERROR_DOMAIN_ADD_USER`, {
        modalType: ModalTypes.ERROR_MODAL,
        modalProps: {
          titleMessageKey: 'errors.error',
          errorMessageKey: 'errors.addUserDomainError',
        }
      }));
    }
  }

  public onDeleteDomainUserRequest(user: IDomainUserInDTO) {
    const { dispatch } = this.props;
    dispatch<any>(ModalsActions.showModal(`DELETE_DOMAIN_USER_CONFIRM_MODAL`, {
      modalType: ModalTypes.CONTENT_MODAL,
      modalProps: {
        content: (
        <UserDeleteForm
          user={user}
          onConfirmDeleteUser={(anonymizeAllEvents, deleteAllStandardDevices) => this.onDeleteDomainUserConfirm(user, anonymizeAllEvents, deleteAllStandardDevices)}
        />
        ),
      }
    }));
  }

  async onDeleteDomainUserConfirm(user: IDomainUserInDTO, anonymizeAllEvents: boolean, deleteAllStandardDevices: boolean) {
    const { dispatch } = this.props;
    try {
      dispatch<any>(SettingsActions.setSpinnerVisible(true));
      await dispatch<any>(DomainsActions.deleteDomainUser(user, anonymizeAllEvents, deleteAllStandardDevices));
      dispatch<any>(ModalsActions.hideModal('DELETE_DOMAIN_USER_CONFIRM_MODAL'));
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      await dispatch<any>(DomainsActions.fetchSofiaDomainUsers({ pageSize: 50 }));
      await dispatch<any>(DomainsActions.fetchSofiaDomainInstallers({ pageSize: 50 }));
      await dispatch<any>(DomainsActions.fetchDomainWalletSubscriptions());
      await dispatch<any>(DomainsActions.fetchSofiaDomainSubscription());
    } catch (error) {
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      dispatch<any>(ModalsActions.showModal(`ERROR_DOMAIN_ADD_USER`, {
        modalType: ModalTypes.ERROR_MODAL,
        modalProps: {
          titleMessageKey: 'errors.error',
          errorMessageKey: 'errors.deleteDomainUserError',
        }
      }));
    }
  }

  public handleChangeRowsPerPage(pageSize) {
    this.setState({ pageSize }, () => {
      this.onFetchDomains(0, pageSize)
    });
  }

  public async onFetchDomainsOnPage(page: number) {
    const { pageSize } = this.state;
    this.onFetchDomains(page, pageSize);
  }

  public async onSearchTextChange(text: string) {
    const { pageSize } = this.state;
    this.setState({ searchText: text }, async () => await this.onFetchDomains(0, pageSize));
  }

  public async onSearchReset() {
    const { pageSize } = this.state;
    this.setState({ searchText: '' }, async () => await this.onFetchDomains(0, pageSize));
  }

  public async onExportManagersCSV() {
    const { dispatch } = this.props;
    dispatch<any>(SettingsActions.setSpinnerVisible(true));
    try {
      await dispatch<any>(DomainsActions.exportManagersAndSubscriptionsCSV());
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
    } catch (error) {
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
    }
  }

  public onEnableIrisRequest(plant: IDomainDetailedDTO) {
    const { dispatch } = this.props;
    dispatch<any>(ModalsActions.showModal(`ENABLE_IRIS_PLANT_CONFIRM_MODAL`, {
      modalType: ModalTypes.CONFIRM_MODAL,
      modalProps: {
        icon: <WarningIcon style={{ color: 'orange', fontSize: 50 }} />,
        titleMessageKey: 'domains.domainIrisEnable',
        successMessageKey: 'domains.domainIrisEnableConfirm',
        confirmMessageKey: 'forms.confirm',
        cancelMessageKey: 'forms.cancel',
        onConfirm: () => this.onEnableIrisConfirm(plant),
        onCancel: () => dispatch<any>(ModalsActions.hideModal(`ENABLE_IRIS_PLANT_CONFIRM_MODAL`)),
      }
    }));
  }

  public async onEnableIrisConfirm(plant: IDomainDetailedDTO) {
    const { dispatch } = this.props;
    try {
      dispatch<any>(ModalsActions.hideModal(`ENABLE_IRIS_PLANT_CONFIRM_MODAL`));
      dispatch<any>(SettingsActions.setSpinnerVisible(true));
      await dispatch<any>(DomainActions.enableIris(plant.id));
      await dispatch<any>(CreditsActions.fetchCustomerWalletPlants({ walletId: undefined, page: 0, pageSize: 5000 }, true));
      dispatch<any>(ModalsActions.hideModal('DOMAIN_DETAILS_MODAL'));
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
    } catch (error) {
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      dispatch<any>(ModalsActions.showModal(`ERROR_DOMAIN_DELETION`, {
        modalType: ModalTypes.ERROR_MODAL,
        modalProps: {
          titleMessageKey: 'errors.error',
          errorMessageKey: 'errors.enableIrisDomainRequestError',
        }
      }));
    }
  }

  public onOpenEditPlantSocDetailsForm(domain: IDomainDetailedDTO) {
    const { dispatch } = this.props;
    dispatch<any>(ModalsActions.showModal(`OPERATIONAL_EDIT_PLANT_DETAILS_MODAL`, {
      modalType: ModalTypes.OPERATIONAL_VIEW_MODAL,
      modalProps: {
        content: (
          <EditPlantSOCDetailsForm
            initialData={{
              zenDeskId: domain.zenDeskId || '',
              lockbaseId: domain.lockbaseId || '',
              license1ris: domain.license1ris || '',
            }}
            onSubmit={(data: IPlantDetailsUpdateDTO) => this.onUpdatePlantSocDetails(domain.id, data)}
          />
        ),
        titleMessageKey: 'domains.editPlantDetails',
      }
    }));
  }

  public async onUpdatePlantSocDetails(plantId: number, data: IPlantDetailsUpdateDTO) {
    const { dispatch, detailedDomain } = this.props;
    try {
      dispatch<any>(ModalsActions.hideModal(`OPERATIONAL_EDIT_PLANT_DETAILS_MODAL`));
      dispatch<any>(SettingsActions.setSpinnerVisible(true));
      await dispatch<any>(DomainsActions.updatePlantDetails(plantId, data));
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      dispatch<any>(ModalsActions.showModal(`SUCCESS_MODAL`, {
        modalType: ModalTypes.SUCCESS_MODAL,
        modalProps: {
          titleMessageKey: 'domains.editPlantDetailsSuccess',
        }
      }));
      const updatedDomain = {
        ...detailedDomain,
        zenDeskId: data.zenDeskId,
        lockbaseId: data.lockbaseId,
        license1ris: data.license1ris,
      };
      dispatch<any>(DomainsBaseAction.setSelectedDetailedDomain(updatedDomain));
      await this.onFetchDomains();
    } catch (error) {
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      dispatch<any>(ModalsActions.showModal(`ERROR_UPDATE_PLANT_DETAILS`, {
        modalType: ModalTypes.ERROR_MODAL,
        modalProps: {
          titleMessageKey: 'errors.error',
          errorMessageKey: 'errors.updatePlantDetailsError',
        }
      }));
    }
  }

  onTabChange(tab) {
    this.setState({ activeTab: tab }, () => this.onFetchDomains());
  }

  render() {
    const { classes, domains, legacyDomains, periodicDomains } = this.props
    const { pageSize, activeTab } = this.state;
    const data = activeTab === 0 ? ((domains && domains.content) || []) : activeTab === 1 ? ((periodicDomains && periodicDomains.content) || []) : ((legacyDomains && legacyDomains.content) || []);
    const pagination = activeTab === 0 ? ((domains && domains.pagination) || []) : activeTab === 1 ? ((periodicDomains && periodicDomains.pagination) || []) : ((legacyDomains && legacyDomains.pagination) || []);
    this.domainsColumns = [
      {
        field: 'id',
        headerName: 'App Id',
        type: 'string',
        sortable: true,
        filterable: true,
        width: 60,
        renderCell: (params) => {
          const domain = params.row;
          const isPeriodic = domain?.modality === 'PERIODIC';
          return (
            <div>
              <h3 style={{ margin: 0, fontSize: 13, whiteSpace: 'normal' }}>{isPeriodic ? "-" : domain.appId}</h3>
            </div>
        )}
      },
      {
        field: 'name',
        headerName: translations.t('forms.domainName'),
        type: 'string',
        sortable: true,
        filterable: true,
        flex: 1,
        minWidth: 150,
        renderCell: (params) => {
          const domain = params.row;
          const federatedMasterDomain = domain?.subscriptionDomainHostname ? getDomainNameFromHostname(domain?.subscriptionDomainHostname) : false;
          const isMasterDomainOfFederation = federatedMasterDomain && federatedMasterDomain === domain?.name;
          return (
            <div>
              <h3 className={classes.rowTitle}>{domain?.name}</h3>
              {federatedMasterDomain ? (
                <div className={classes.federatedContainer}>
                  <Business style={{ fontSize: 15, marginBottom: 5 }} />
                  {isMasterDomainOfFederation ? (
                    <h4 style={{ marginTop: 0}}><ReduxLanguage languageKey="domains.federatedMasterDomain" /></h4>
                  ) : (
                    <h4 style={{ marginTop: 0}}><ReduxLanguage languageKey="domains.federatedDomain" languageProps={{ domain: federatedMasterDomain }}/></h4>
                  )}
                </div>
              ): null}
            </div>
          )
        }
      },
      {
        field: 'hostname',
        headerName: translations.t('forms.hostName'),
        type: 'string',
        sortable: false,
        filterable: false,
        minWidth: 150,
        flex: 1,
      },
      {
        field: 'serviceName',
        headerName: translations.t('forms.domainType'),
        type: 'string',
        sortable: true,
        filterable: true,
        minWidth: 180,
        renderCell: (params) => {
          const domain = params.row;
          return (
            <div style={{display:'flex', flexDirection:'row', alignItems:'center'}}>
              <h4 style={{ marginRight: 0 }}><ReduxLanguage languageKey={domain?.modality === 'PERIODIC' ? 'credits.domainPeriodic' : domain?.cloudCredits ? 'domains.domainWithCloudCredits' : 'domains.domainStandard'} /></h4>
              {domain && (domain.enable1ris === DomainIrisStatusOptions.ENABLED) ? (
                <h4>
                 - <ReduxLanguage languageKey={'domains.domainIrisEnabled'} />
                </h4>
              ) : null}
            </div>
          )
        }
      },
      {
        field: 'segmentType',
        headerName: translations.t('forms.segment'),
        type: 'string',
        sortable: true,
        filterable: true,
        minWidth: 150,
        flex: 1,
        renderCell: (params) => {
          const domain = params.row;
          const domainSegment = domain.subSegment ? domain.subSegment : domain.segmentType;
          return (
            <div style={{display:'flex', flexDirection:'row', alignItems:'center'}}>
              <h4 style={{ marginRight: 0 }}><ReduxLanguage languageKey={domainSegment ? `segmentTypes.${domainSegment}_name` : '---'} /></h4>
            </div>
          )
        }
      },
      {
        field: 'subscriptionDomainCompanyId',
        headerName: translations.t('forms.actions'),
        type: 'string',
        sortable: true,
        filterable: true,
        width: 150,
        renderCell: (params) => {
          const domain = params.row;
          return (
            <div>
            {domain && (domain.enable1ris === DomainIrisStatusOptions.REQUIRED || domain.enable1ris === DomainIrisStatusOptions.ENABLED) ? (
              <div className={classes.domainIrisContainer}>
                {(domain.enable1ris === DomainIrisStatusOptions.REQUIRED && AbilityProvider.getAbilityHelper().hasPermission(PERMISSIONS.DOMAIN_IRIS_ENABLE)) ? (
                  <Button className={classes.domainIrisInnerContainer} style={{ backgroundColor: ORANGE, boxShadow: 'rgba(0, 0, 0, 0.2) 0px 3px 1px -2px' }}
                    onClick={(e)=>{e.stopPropagation(); this.onEnableIrisRequest(domain);}}>
                    <h5 className={classes.domainTypeText} style={{ color: 'white', paddingLeft: 10, paddingRight: 10 }}>
                      <ReduxLanguage languageKey={'domains.domainIrisEnable'} />
                    </h5>
                  </Button>
                ):null}
              </div>
            ) : null}
          </div>
        )}
      }
    ]
    return (
      <div className={classes.container}>
        <div className={classes.header}>
          <SearchBar
            searchTextTranslationKey="domains.domainName"
            newEntityTranslationKey="forms.createNewDomain"
            searchDebounceDelay={500}
            onSearchTextChange={(text: string) => this.onSearchTextChange(text)}
          />
          <Permission abilityHelper={AbilityProvider.getAbilityHelper()} permission={PERMISSIONS.DOMAIN_EXPORT_ALL_MANAGERS}>
            <Button
              variant="contained"
              style={{ backgroundColor: 'cornflowerblue', color: 'white', marginLeft: 10, height: 37, fontWeight: 'bold'  }}
              onClick={() => this.onExportManagersCSV()}
            >
              <ReduxLanguage languageKey="credits.exportManagersCSV" />
            </Button>
          </Permission>
        </div>
        <Permission abilityHelper={AbilityProvider.getAbilityHelper()} permission={PERMISSIONS.DOMAIN_LEGACY_DOMAINS_READ}>
          <AppBar position="sticky" style={{ backgroundColor: 'white', marginTop: 0 }}>
            <Tabs
              value={activeTab}
              indicatorColor="primary"
              textColor="primary"
              style={{ backgroundColor: 'white', width: '100%' }}
              onChange={(e, index) => this.onTabChange(index)}
            >
              <Tab icon={<CreditsIcon style={{ fontSize: 25 }} />} label={<h5 style={{ margin: 0 }}><ReduxLanguage languageKey="domains.domainsWithCloudCredits" /></h5>} />
              <Tab icon={<CachedIcon style={{ fontSize: 25 }} />} label={<h5 style={{ margin: 0 }}><ReduxLanguage languageKey="credits.domainsPeriodic" /></h5>} />
              <Tab icon={<AccountBalanceOutlined style={{ fontSize: 25 }} />} label={<h5 style={{ margin: 0 }}><ReduxLanguage languageKey="domains.domainsLegacy" /></h5>} />
            </Tabs>
          </AppBar>
        </Permission>
        <div className={classes.tableContainer}>
          <DataGrid
            getRowId={(d) => d.id != null ? d.id : d.hostname}
            rows={data}
            columns={this.domainsColumns}
            rowHeight={60}
            page={(pagination as PaginationDTO)?.number || 0}
            rowCount={(pagination as PaginationDTO)?.totalElements || 0}
            pageSize={pageSize}
            loading={this.state.isLoading}
            paginationMode="server"
            rowsPerPageOptions={[25, 50, 100]}
            onPageSizeChange={(pageSize) => this.handleChangeRowsPerPage(pageSize)}
            onPageChange={page => this.onFetchDomainsOnPage(page)}
            onRowClick={row => this.onRowClick(row.row)}
            componentsProps={{
              pagination: {
                labelRowsPerPage: translations.t('forms.rowsPerPage')
              }
            }}
            components={{
              NoRowsOverlay: () => (
                <Stack height="100%" alignItems="center" justifyContent="center">
                  <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center'}}>
                    <SearchOutlined style={{ alignSelf: 'center' }} />
                    <h3 style={{ textAlign: 'center', fontWeight: 'bold', marginBottom: 0 }}><ReduxLanguage languageKey='domains.noDomainsFoundTitle' /></h3>
                    <h4 style={{ textAlign: 'center', marginTop: 0 }}><ReduxLanguage languageKey='domains.noDomainFoundDescription' /></h4>
                  </div>
                </Stack>
              ),
              NoResultsOverlay: () => (
                <Stack height="100%" alignItems="center" justifyContent="center">
                  <h3 style={{ textAlign: 'center', fontWeight: 'bold', marginBottom: 0 }}><ReduxLanguage languageKey='domains.noDomainsFoundTitle' /></h3>
                  <h4 style={{ textAlign: 'center', marginTop: 0 }}><ReduxLanguage languageKey='domains.noDomainFoundDescription' /></h4>
                </Stack>
              )
            }}
            sx={{
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
            }}
          />
        </div>
      </div>
    );
  }
}

function mapStateToProps(state: IState) {
  return {
    language: state.settings.language,
    legacyDomains: state.domains.domains,
    periodicDomains: state.credits.periodicDomains.data,
    domains: state.credits.domains.data,
    detailedDomain: state.domains.selectedDetailedDomain,
    products: state.products.products,
  };
}

const connector = connect(mapStateToProps);

export default connector(withRouter(withStyles(styles)(DomainsPage)));
